import { getAuth, Auth, User } from "firebase/auth";

/**
 * Mock User object for testing
 */
export interface MockUser extends Partial<User> {
  uid: string;
  email?: string | null;
  displayName?: string | null;
}

/**
 * Creates a mock auth object with a mock user
 */
const createMockAuth = (mockUser: MockUser): Auth => {
  return {
    currentUser: mockUser as User,
  } as Auth;
};

/**
 * Wrapper for getAuth() that can return either the real Firebase auth
 * or a mock auth object based on environment configuration.
 *
 * When REACT_APP_TESTING_MODE is true and test auth sign-in was performed in
 * setupTestEnvironment (using REACT_APP_TESTING_OWNER_EMAIL and
 * REACT_APP_TESTING_PASSWORD), returns real Firebase auth with currentUser set.
 * If sign-in was skipped or not configured, returns real getAuth() (currentUser
 * may be null; use skipAuthSetup and mock only when you do not need real tokens).
 *
 * @param mockUser - Optional mock user to use when in test mode and auth setup was skipped
 * @returns Auth object (real or mock)
 */
export const getAuthWrapper = (mockUser?: MockUser): Auth => {
  // In test mode, use real Firebase auth when possible (after setupTestEnvironment
  // signs in with env email/password). No mock wrapper needed.
  const isTestMode = process.env.REACT_APP_TESTING_MODE === "true";

  if (isTestMode) {
    // If real auth already has a user (from setupTestEnvironment sign-in), use it.
    const realAuth = getAuth();
    if (realAuth.currentUser) {
      return realAuth;
    }
    // Otherwise fall back to mock so tests that skip auth setup still work.
    const testOwnerId = process.env.REACT_APP_TESTING_OWNER_ID;
    const testOwnerEmail = process.env.REACT_APP_TESTING_OWNER_EMAIL;

    const user: MockUser = mockUser || {
      uid: testOwnerId || "test-user-id",
      email: testOwnerEmail || "test@example.com",
    };

    return createMockAuth(user);
  }

  // Otherwise, return the real Firebase auth
  return getAuth();
};
